import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { billingOpsAPI, soCreateAPI, vendorAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash, MagnifyingGlass, FloppyDisk, ClipboardText, Eye, ArrowRight, PencilSimple,
} from '@phosphor-icons/react';
import ItemSearchPicker from '@/components/ItemSearchPicker';

const emptyLine = () => ({ item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, tax_percent: 0 });

export default function PurchaseOrdersPage() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [converting, setConverting] = useState(false);

  const [form, setForm] = useState({
    vendor_name: '', vendor_id: '', branch_id: '', po_date: new Date().toISOString().split('T')[0],
    expected_delivery: '', reference: '', notes: '',
  });
  const [lineItems, setLineItems] = useState([emptyLine()]);

  const loadPOs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await billingOpsAPI.listPurchaseOrders({ page, search, branch_id: branchFilter, status: statusFilter });
      setPos(data.purchase_orders || []);
      setTotalPages(data.total_pages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, search, branchFilter, statusFilter]);

  useEffect(() => { loadPOs(); }, [loadPOs]);

  useEffect(() => {
    (async () => {
      const [brRes, itemsRes, venRes] = await Promise.allSettled([
        soCreateAPI.branches(),
        soCreateAPI.items({ per_page: 2000 }),
        vendorAPI.getAll(),
      ]);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
      if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
      if (venRes.status === 'fulfilled') setVendors(venRes.value.data.vendors || []);
    })();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ vendor_name: '', vendor_id: '', branch_id: '', po_date: new Date().toISOString().split('T')[0], expected_delivery: '', reference: '', notes: '' });
    setLineItems([emptyLine()]);
  };

  const handleEditPO = (po) => {
    setEditingId(po.id);
    setForm({
      vendor_id: po.vendor_id || '', vendor_name: po.vendor_name || '', branch_id: po.branch_id || '',
      po_date: po.po_date || new Date().toISOString().split('T')[0], expected_delivery: po.expected_delivery || '',
      reference: po.reference || '', notes: po.notes || '',
    });
    setLineItems((po.line_items || []).map(li => ({
      item_id: li.item_id || '', name: li.name || '', product_code: li.product_code || '', hsn_sac: li.hsn_sac || '',
      quantity: li.quantity || 1, rate: li.rate || 0, discount_percent: li.discount_percent || 0, tax_percent: li.tax_percent || 0,
    })) || [emptyLine()]);
    setShowDetail(null);
    setShowCreate(true);
  };

  const selectCatalogItem = (idx, itemId) => {
    const item = catalogItems.find(i => i.id === itemId);
    if (!item) return;
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li, item_id: item.id, name: item.name, product_code: item.product_code || '',
      hsn_sac: item.hsn_sac_code || '', rate: item.purchase_price || item.cost_price || item.mrp || 0, tax_percent: gstRate,
    } : li));
  };

  const updateLineItem = (idx, field, value) => setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  const addLineItem = () => setLineItems(prev => [...prev, emptyLine()]);
  const removeLineItem = (idx) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== idx)); };

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSavePO = async () => {
    if (!form.vendor_name) { toast.error('Vendor name is required'); return; }
    if (!form.branch_id) { toast.error('Branch is required'); return; }
    if (!lineItems.some(li => li.name)) { toast.error('At least one item is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: lineItems.filter(li => li.name).map(li => ({
          item_id: li.item_id, name: li.name, product_code: li.product_code, hsn_sac: li.hsn_sac,
          quantity: parseFloat(li.quantity) || 1, rate: parseFloat(li.rate) || 0,
          discount_percent: parseFloat(li.discount_percent) || 0, tax_percent: parseFloat(li.tax_percent) || 0,
        })),
      };
      if (editingId) {
        await billingOpsAPI.updatePurchaseOrder(editingId, payload);
        toast.success('Purchase Order updated!');
      } else {
        const { data } = await billingOpsAPI.createPurchaseOrder(payload);
        toast.success(`Purchase Order ${data.po_number} created!`);
      }
      setShowCreate(false);
      resetForm();
      loadPOs();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${editingId ? 'update' : 'create'} purchase order`);
    } finally { setSaving(false); }
  };

  const handleDeletePO = async (po) => {
    if (!window.confirm(`Delete purchase order "${po.po_number}"?`)) return;
    try {
      await billingOpsAPI.deletePurchaseOrder(po.id);
      toast.success('Purchase order deleted');
      loadPOs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleConvert = async (po) => {
    const billNumber = window.prompt(`Convert ${po.po_number} to a Vendor Bill.\n\nEnter the vendor's Bill Number:`);
    if (!billNumber) return;
    setConverting(true);
    try {
      const { data } = await billingOpsAPI.convertPOToBill(po.id, { bill_number: billNumber });
      toast.success(`Vendor Bill ${data.bill.bill_number} created & stock received`);
      setShowDetail(null);
      loadPOs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
    finally { setConverting(false); }
  };

  const statusBadge = (s) => {
    const map = { open: 'bg-blue-50 text-blue-700', billed: 'bg-green-50 text-green-700', cancelled: 'bg-gray-100 text-gray-600' };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${map[s] || 'bg-amber-50 text-amber-700'}`}>{s}</span>;
  };

  return (
    <div className="p-6" data-testid="purchase-orders-page">
      <PageHeader title="Purchase Orders" subtitle="Raise purchase orders to vendors, then convert to a vendor bill on receipt" />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search POs..." className="pl-9" data-testid="po-search" />
        </div>
        <SearchableSelect value={branchFilter || ''} onChange={v => { setBranchFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Branches' }, ...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]}
          placeholder="All Branches" testid="po-branch-filter" buttonClassName="w-[200px]" />
        <Select value={statusFilter || 'all'} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="po-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="billed">Billed</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={() => { resetForm(); setShowCreate(true); }} data-testid="po-create-btn">
          <Plus size={14} className="mr-1" /> New Purchase Order
        </Button>
      </div>

      <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="po-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
              <th className="text-left p-3">PO #</th>
              <th className="text-left p-3">Vendor</th>
              <th className="text-left p-3">Branch</th>
              <th className="text-left p-3">PO Date</th>
              <th className="text-right p-3">Grand Total</th>
              <th className="text-center p-3">Status</th>
              <th className="p-3 w-[160px]"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : pos.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-[#64748B]">
                <ClipboardText size={28} className="mx-auto mb-2 opacity-40" />
                No purchase orders yet. Create one to get started.
              </td></tr>
            ) : pos.map(po => (
              <tr key={po.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`po-row-${po.id}`}>
                <td className="p-3 font-medium text-[#0F4C81]">{po.po_number}</td>
                <td className="p-3">{po.vendor_name}</td>
                <td className="p-3 text-xs">{po.branch_name}</td>
                <td className="p-3 text-xs">{po.po_date}</td>
                <td className="p-3 text-right font-medium">{fmt(po.grand_total)}</td>
                <td className="p-3 text-center">
                  {po.converted_to_bill_id ? (
                    <span className="text-[10px] text-emerald-600 font-medium" title={po.converted_to_bill_number}>Billed</span>
                  ) : statusBadge(po.status)}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    {!po.converted_to_bill_id && (
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-blue-600 hover:bg-blue-50 px-2"
                        disabled={converting} onClick={() => handleConvert(po)} data-testid={`po-convert-${po.id}`}>
                        <ArrowRight size={12} className="mr-0.5" /> To Bill
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowDetail(po)} data-testid={`po-view-${po.id}`}>
                      <Eye size={14} />
                    </Button>
                    {!po.converted_to_bill_id && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDeletePO(po)} data-testid={`po-delete-${po.id}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-[#E2E8F0]">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-xs text-[#64748B]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="po-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardText size={18} /> Purchase Order: {showDetail?.po_number}
            </DialogTitle>
          </DialogHeader>
          {showDetail && (
            <div className="space-y-4 text-sm">
              <div className="flex justify-end gap-2">
                {!showDetail.converted_to_bill_id && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => handleEditPO(showDetail)} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="po-edit-btn">
                      <PencilSimple size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" disabled={converting} onClick={() => handleConvert(showDetail)} data-testid="po-detail-convert-btn">
                      <ArrowRight size={14} className="mr-1" /> {converting ? 'Converting...' : 'Convert to Vendor Bill'}
                    </Button>
                  </>
                )}
                {showDetail.converted_to_bill_id && (
                  <span className="text-xs text-emerald-600 font-medium self-center">Converted to Bill: {showDetail.converted_to_bill_number}</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div><span className="text-[#64748B]">Vendor:</span> <strong>{showDetail.vendor_name}</strong></div>
                <div><span className="text-[#64748B]">Branch:</span> {showDetail.branch_name}</div>
                <div><span className="text-[#64748B]">PO Date:</span> {showDetail.po_date}</div>
                <div><span className="text-[#64748B]">Expected:</span> {showDetail.expected_delivery || '-'}</div>
                <div><span className="text-[#64748B]">Reference:</span> {showDetail.reference || '-'}</div>
                <div><span className="text-[#64748B]">Grand Total:</span> <strong>{fmt(showDetail.grand_total)}</strong></div>
              </div>
              {showDetail.line_items?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[#64748B] mb-2">Line Items</h4>
                  <table className="w-full text-xs">
                    <thead><tr className="bg-[#F8FAFC]">
                      <th className="text-left p-2">Item</th><th className="text-left p-2">HSN</th>
                      <th className="text-center p-2">Qty</th><th className="text-right p-2">Rate</th>
                      <th className="text-right p-2">Tax</th><th className="text-right p-2">Total</th>
                    </tr></thead>
                    <tbody>
                      {showDetail.line_items.map((li, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-2">{li.name}</td>
                          <td className="p-2">{li.hsn_sac || '-'}</td>
                          <td className="p-2 text-center">{li.quantity}</td>
                          <td className="p-2 text-right">{fmt(li.rate)}</td>
                          <td className="p-2 text-right">{li.tax_percent || 0}%</td>
                          <td className="p-2 text-right font-medium">{fmt(li.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {showDetail.notes && <p className="text-xs text-[#64748B]"><strong>Notes:</strong> {showDetail.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Vendor *</label>
                <SearchableSelect value={form.vendor_name} onChange={v => setForm(f => ({ ...f, vendor_name: v }))} placeholder="Select vendor..." testid="po-vendor-name" buttonClassName="w-full"
                  options={[...vendors.map(v => ({ value: v.name, label: v.display_name || v.name }))]} />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Branch *</label>
                <SearchableSelect value={form.branch_id} onChange={v => setForm(f => ({ ...f, branch_id: v }))} placeholder="Select branch..." testid="po-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]} />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">PO Date</label>
                <Input type="date" value={form.po_date} onChange={e => setForm(f => ({ ...f, po_date: e.target.value }))} data-testid="po-date" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Expected Delivery</label>
                <Input type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))} data-testid="po-expected" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Reference</label>
                <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Quote / ref no." data-testid="po-reference" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes" data-testid="po-notes" />
              </div>
            </div>

            <h4 className="text-xs font-semibold text-[#0F172A]">Line Items</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="po-items-table">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                    <th className="text-left p-2 w-[240px]">Item</th>
                    <th className="text-center p-2 w-[60px]">Qty</th>
                    <th className="text-right p-2 w-[90px]">Rate</th>
                    <th className="text-center p-2 w-[60px]">Tax%</th>
                    <th className="p-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`po-create-line-${idx}`}>
                      <td className="p-2">
                        <ItemSearchPicker items={catalogItems} value={li.name} onPick={(item) => selectCatalogItem(idx, item.id)} testid={`po-item-select-${idx}`} />
                      </td>
                      <td className="p-2"><Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="text-center h-8 text-xs" data-testid={`po-qty-${idx}`} /></td>
                      <td className="p-2"><Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} className="text-right h-8 text-xs" data-testid={`po-rate-${idx}`} /></td>
                      <td className="p-2"><Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', e.target.value)} className="text-center h-8 text-xs" data-testid={`po-tax-${idx}`} /></td>
                      <td className="p-2">
                        {lineItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLineItem(idx)}><Trash size={14} /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="outline" size="sm" onClick={addLineItem} data-testid="po-add-item">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleSavePO} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="po-submit-btn">
                <FloppyDisk size={16} className="mr-2" />{saving ? (editingId ? 'Updating...' : 'Creating...') : (editingId ? 'Update PO' : 'Create PO')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
